import type { StyleObject } from '@/typings/secure-fields';

export const enum Environment {
  Production = 'production',
  Stage = 'stage',
  Dukenukem = 'dukenukem',
  Development = 'development'
}

// Property default language code
export const defaultLanguageCode = 'en-US';
export const defaultCurrencyCode = 'EUR';
export const accessTokenCookieName = 'bobw-jwt-token';
export const helpEmail = 'hello@bobw.co';
export const businessEmail = 'biz@bobw.co';
export const loyaltyVoucher = 'innercircle';
export const innerCircleRateToggledQueryParam = 'isInnerCircleRateToggled';
export const freeCancellationToggledQueryParam = 'isFreeCancellationToggled';
export const upgradeFromQueryParam = 'upgradedFrom';
export const voucherQueryParam = 'voucher';
export const guestBookToggledQueryParam = 'isGuestbookToggled';
export const guestBookUtmSource = 'theguestbook';
export const turnstileContainerId = 'turnstile-container';
export const mainContentRefKey = 'mainContentRef';
// Derbysoft tracking parameter, required for commercial purposes
export const dsclIdQueryParam = 'dsclid';
export const sortQueryParam = 'sort';

// Language cookie name
export const languageCookieName = 'selectedLanguage';

// {X}% discount for inner circle members
export const defaultInnerCircleDiscount = 5;
export const referralInnerCircleDiscount = 10;

export const globalReviewScore = 4.6;
export const globalReviewCount = 9237;

export const environmentNames = {
  [Environment.Production]: 'prod',
  [Environment.Stage]: 'stage',
  [Environment.Dukenukem]: 'dukenukem',
  [Environment.Development]: 'development'
} as const;

export const baseUrls = {
  [Environment.Production]: 'https://bobw.co',
  [Environment.Stage]: 'https://preview.bobw.co',
  [Environment.Dukenukem]: 'https://preview.bobw.co',
  [Environment.Development]: 'http://localhost:3001'
} as const;

export const baseBookingUrls = {
  [Environment.Production]: 'https://booking.bobw.co',
  [Environment.Stage]: 'https://booking.staging-bobw.com',
  [Environment.Dukenukem]: 'https://booking.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3001'
} as const;

export const coreApiBaseUrls = {
  [Environment.Production]: 'https://core-api.bobw.co',
  [Environment.Stage]: 'https://core-api.staging-bobw.com',
  [Environment.Dukenukem]: 'https://core-api.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3030'
} as const;

export const webflowApiBaseUrls = {
  [Environment.Production]: 'https://webflow-proxy.bobw.co',
  [Environment.Stage]: 'https://webflow-proxy.staging-bobw.com',
  [Environment.Dukenukem]: 'https://webflow-proxy.staging-bobw.com',
  [Environment.Development]: 'http://localhost:8787'
} as const;

export const checkInBaseUrls = {
  [Environment.Production]: 'https://guest.bobw.co',
  [Environment.Stage]: 'https://guest.staging-bobw.com',
  [Environment.Dukenukem]: 'https://guest.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:5002'
} as const;

export const receptionBaseUrls = {
  [Environment.Production]: 'https://reception.bobw.co',
  [Environment.Stage]: 'https://reception.staging-bobw.com',
  [Environment.Dukenukem]: 'https://reception.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3000'
} as const;

export const secureFieldsHosts = {
  [Environment.Production]: 'pay.datatrans.com',
  [Environment.Stage]: 'pay.sandbox.datatrans.com',
  [Environment.Dukenukem]: 'pay.sandbox.datatrans.com',
  [Environment.Development]: 'pay.sandbox.datatrans.com'
} as const;

/*
 * Test account has been created for roman@bobw.co with tag id "GTM-5JRNN6G".
 * It could be changed to anything else during testing.
 */
export const gtmIds = {
  [Environment.Production]: 'GTM-M57TPL5',
  [Environment.Stage]: 'GTM-5JRNN6G',
  [Environment.Dukenukem]: 'GTM-5JRNN6G',
  [Environment.Development]: 'GTM-5JRNN6G'
} as const;

export const turnstileSiteKeys = {
  [Environment.Production]: '0x4AAAAAAAg5ZgJYolrIRZel',
  [Environment.Stage]: '0x4AAAAAAAg5ZFPZnqqIPBiJ',
  [Environment.Dukenukem]: '0x4AAAAAAAg5ZQ7WkSDXfbS6',
  [Environment.Development]: '0x4AAAAAAAg5ZQ7WkSDXfbS6'
} as const;

export const innerCirclePaths = {
  signUp: '/signup',
  logIn: '/login',
  account: '/account'
};

export const routes = {
  index: '/',
  booking: {
    index: '/',
    properties: '/properties'
  },
  widgets: '/widgets',
  components: '/components',
  reservation: {
    index(reservationGroupId?: string): string {
      if (reservationGroupId === undefined) {
        return '/reservation/:reservationGroupId';
      }

      return `/reservation/${reservationGroupId}`;
    },
    retryPayment(reservationGroupId?: string): string {
      if (reservationGroupId === undefined) {
        return '/reservation/:reservationGroupId/retry-payment';
      }

      return `/reservation/${reservationGroupId}/retry-payment`;
    },
    success(reservationGroupId?: string): string {
      if (reservationGroupId === undefined) {
        return '/reservation/:reservationGroupId/success';
      }

      return `/reservation/${reservationGroupId}/success`;
    },
    failed(reservationGroupId?: string): string {
      if (reservationGroupId === undefined) {
        return '/reservation/:reservationGroupId/failed';
      }

      return `/reservation/${reservationGroupId}/failed`;
    }
  },

  confirmation: '/confirmation',
  bookingAvailabilityError: '/availability-error'
} as const;

export const secureFieldsPaymentMethods = {
  visa: 'VIS',
  mastercard: 'ECA',
  amex: 'AMX',
  diners: 'DIN',
  discover: 'DIS',
  jcb: 'JCB',
  elo: 'ELO',
  unionpay: 'CUP'
} as const;

export const secureFieldsStyles: StyleObject = {
  '@font-face': {
    '*': {
      fontFamily: 'TheFuture',
      fontWeight: 400,
      fontStyle: 'normal',
      fontDisplay: 'auto',
      src: 'local("TheFuture-Regular"), url("TheFuture-Regular.ttf") format("truetype")'
    }
  },

  '*': {
    fontFamily: 'TheFuture, sans-serif',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '28px 16px 12px 24px',
    background: 'none',
    border: 'none',
    outline: 'none',
    width: '100%'
  }
};

export const defaultGuestCounts = {
  adults: 1,
  children: 0
};

export const globalReviewProperties = [
  // Bob W Commercial House, london-cs43
  'daf8a9c1-f55f-4a8e-8511-aed8009e9c96',
  // Bob W Koti Ullanlinna, helsinki-pun4
  '5e393d16-2255-47bd-a85d-af86009cc048',
  // Bob W Telliskivi, bob-w-telliskivi
  '4d1805ca-8676-484e-872d-aa6400cf60cf',
  // Bob W Gamle Oslo, oslo-gro16
  '0fd902b4-889d-47f4-931e-af97009fc006',
  // Bob W Turku City Center, turku-eer12
  'c25ddc97-d2fa-4e96-8319-b0c800f089fa'
];

export const segmentKeys = {
  [Environment.Production]: 'FpDX2n8R3TNNHhwBWDo94JCjGGQGkZVt',
  [Environment.Stage]: 'pGyW7G2koNsBoOnJapGFTQbkEdJOiEsX',
  [Environment.Dukenukem]: '8uhbAbNjyVuWOwF7afcYPjwLbDokUX2J',
  [Environment.Development]: 'E1yCGAvBtCOCF3tFKF6woJXhMh7gM58Z'
} as const;

export const pageNames = {
  bookingUnitCategories: 'Booking Listing Unit Categories',
  widgets: 'Widgets',
  components: 'Components',
  reservationRetryPayment: 'Booking Payment Failed',
  reservationCompleted: 'Booking Success',
  reservationFailed: 'Booking Error',
  bookingConfirmation: 'Booking Confirmation',
  bookingAvailabilityError: 'Booking Unit Category Unavailable'
} as const;

export const ucmStorageKey = 'uc_gcm';

export const isLanguageDisabled = true;
